import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowButton from "../ArrowButton/ArrowButton";
import "./ContactForm.css";

const EMPTY_FORM = {
    subject: '',
    name: '',
    email: '',
    phone: '',
    institution: '',
    role: ''
};

export default function ContactForm() {
    const { t } = useTranslation();

    const SUBJECT_OPTIONS = [
        { value: "SAAS", label: t('forms.subjects.saas', 'SAAS') },
        { value: "Tecnologia Educacional", label: t('forms.subjects.edutech', 'Tecnologia Educacional') },
        { value: "Licenças GWS", label: t('forms.subjects.gws', 'Licenças GWS') },
        { value: "Projetos de Engenharia", label: t('forms.subjects.engineering', 'Projetos de Engenharia') }
    ];

    const INSTITUTION_OPTIONS = [
        { value: "Estado/Governo Federal", label: t('forms.institutions.government', 'Estado/Governo Federal') },
        { value: "Prefeitura", label: t('forms.institutions.city_hall', 'Prefeitura') },
        { value: "Secretaria Municipal/Estadual", label: t('forms.institutions.department', 'Secretaria Municipal/Estadual') },
        { value: "Universidade Pública", label: t('forms.institutions.public_uni', 'Universidade Pública') },
        { value: "Universidade Privada", label: t('forms.institutions.private_uni', 'Universidade Privada') },
        { value: "Empresa Privada", label: t('forms.institutions.private_company', 'Empresa Privada') },
        { value: "Fundação Pública", label: t('forms.institutions.public_foundation', 'Fundação Pública') },
        { value: "Entidade sem fins lucrativos", label: t('forms.institutions.non_profit', 'Entidade sem fins lucrativos') },
        { value: "Outros", label: t('forms.institutions.others', 'Outros') }
    ];

    const ROLE_OPTIONS = [
        { value: "Executivo(a)", label: t('forms.roles.executive', 'Executivo(a)') },
        { value: "Gerente", label: t('forms.roles.manager', 'Gerente') },
        { value: "Coordenador(a)", label: t('forms.roles.coordinator', 'Coordenador(a)') },
        { value: "Assessor(a)", label: t('forms.roles.advisor', 'Assessor(a)') },
        { value: "Analista/Especialista", label: t('forms.roles.analyst', 'Analista/Especialista') },
        { value: "Professor(a)", label: t('forms.roles.teacher', 'Professor(a)') },
        { value: "Pesquisador(a)", label: t('forms.roles.researcher', 'Pesquisador(a)') },
        { value: "Secretário(a)", label: t('forms.roles.secretary', 'Secretário(a)') }
    ];

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            const numericValue = value.replace(/\D/g, '');

            let formattedPhone = '';
            if (numericValue.length <= 2) {
                formattedPhone = `(${numericValue}`;
            } else if (numericValue.length <= 7) {
                formattedPhone = `(${numericValue.slice(0, 2)}) ${numericValue.slice(2)}`;
            } else if (numericValue.length <= 11) {
                formattedPhone = `(${numericValue.slice(0, 2)}) ${numericValue.slice(2, 7)}-${numericValue.slice(7)}`;
            } else {
                const limitedValue = numericValue.slice(0, 11);
                formattedPhone = `(${limitedValue.slice(0, 2)}) ${limitedValue.slice(2, 7)}-${limitedValue.slice(7)}`;
            }

            setFormData(prev => ({
                ...prev,
                [name]: numericValue.length === 0 ? '' : formattedPhone
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const validatePhone = (phone: string) => {
        const numericPhone = phone.replace(/\D/g, '');
        return numericPhone.length === 10 || numericPhone.length === 11;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.subject) {
            setValidationError(t('forms.subject_required', 'Selecione um assunto.'));
            return;
        }
        if (!validatePhone(formData.phone)) {
            setValidationError(t('forms.invalid_phone', 'Por favor, insira um telefone válido com 10 ou 11 dígitos.'));
            return;
        }

        setValidationError(null);
        setIsSubmitting(true);
        setSubmitStatus(null);

        const payload = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: formData.institution,
            position: formData.role,
            message: `Assunto: ${formData.subject}`
        };

        try {
            const response = await fetch('https://backend.calriz.com/api/consultor', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setSubmitStatus('success');
                setFormData(EMPTY_FORM);
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Erro ao enviar formulário:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="cz-contact" id="contato">
            <div className="cz-contact__box">
                <div className="cz-contact__intro">
                    <span className="cz-eyebrow cz-eyebrow--on-dark">{t('home.contact_eyebrow', 'Contato')}</span>
                    <h2 className="cz-contact__title">{t('home.contact_title', 'Vamos conversar sobre o seu projeto')}</h2>
                    <p className="cz-contact__text">{t('home.contact_text', 'Conte o que você precisa e um especialista da Calriz retorna o contato.')}</p>
                    <div className="cz-contact__spacer" />
                    <div className="cz-contact__channels">
                        <a href="mailto:contato@calriz.com" className="cz-contact__channel">
                            <span>{t('home.contact_email_label', 'E-mail')}</span>
                            <strong>contato@calriz.com</strong>
                        </a>
                        <a href="tel:+551123947253" className="cz-contact__channel">
                            <span>{t('home.contact_sp_label', 'São Paulo')}</span>
                            <strong>(11) 2394-7253</strong>
                        </a>
                        <a href="tel:+13044700005" className="cz-contact__channel">
                            <span>{t('home.contact_orlando_label', 'Orlando')}</span>
                            <strong>(304) 470-0005</strong>
                        </a>
                    </div>
                </div>

                <div className="cz-contact__card">
                    {submitStatus === 'success' ? (
                        <div className="cz-contact__sent" role="status">
                            <span className="cz-shape cz-shape--drop cz-contact__sent-shape" aria-hidden="true" />
                            <strong>{t('forms.sent_title', 'Mensagem enviada')}</strong>
                            <span>{t('forms.sent_text', 'Obrigado! Nossa equipe entrará em contato em breve.')}</span>
                            <button type="button" className="cz-contact__again" onClick={() => setSubmitStatus(null)}>
                                {t('forms.send_another', 'Enviar outra mensagem')}
                            </button>
                        </div>
                    ) : (
                        <form className="cz-contact__form" onSubmit={handleSubmit}>
                            <div className="cz-contact__field cz-contact__field--full">
                                <span id="cz-subject-label" className="cz-contact__label">
                                    {t('forms.subject_question', 'Sobre o que você quer falar?')}
                                </span>
                                <div className="cz-contact__chips" role="radiogroup" aria-labelledby="cz-subject-label">
                                    {SUBJECT_OPTIONS.map((option) => {
                                        const selected = formData.subject === option.value;
                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                role="radio"
                                                aria-checked={selected}
                                                className={`cz-contact__chip${selected ? ' is-selected' : ''}`}
                                                onClick={() => {
                                                    setFormData(prev => ({ ...prev, subject: option.value }));
                                                    setValidationError(null);
                                                }}
                                                disabled={isSubmitting}
                                            >
                                                {option.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <label className="cz-contact__field">
                                <span className="cz-contact__label">{t('forms.name_label', 'Nome*')}</span>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder={t('forms.name_placeholder', 'Nome e sobrenome')}
                                    autoComplete="name"
                                    required
                                    disabled={isSubmitting}
                                />
                            </label>

                            <label className="cz-contact__field">
                                <span className="cz-contact__label">{t('forms.email_label', 'E-mail corporativo*')}</span>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder={t('forms.email_placeholder', 'contato@suaempresa.com')}
                                    autoComplete="email"
                                    required
                                    disabled={isSubmitting}
                                />
                            </label>

                            <label className="cz-contact__field">
                                <span className="cz-contact__label">{t('forms.phone_label', 'Telefone*')}</span>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder={t('forms.phone_placeholder', '(XX) XXXXX-XXXX')}
                                    autoComplete="tel"
                                    required
                                    disabled={isSubmitting}
                                />
                            </label>

                            <label className="cz-contact__field">
                                <span className="cz-contact__label">{t('forms.role_label', 'Cargo*')}</span>
                                <select name="role" value={formData.role} onChange={handleChange} required disabled={isSubmitting}>
                                    <option value="">{t('forms.select_role', 'Selecione seu cargo')}</option>
                                    {ROLE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>

                            <label className="cz-contact__field cz-contact__field--full">
                                <span className="cz-contact__label">{t('forms.institution_label', 'Instituição*')}</span>
                                <select name="institution" value={formData.institution} onChange={handleChange} required disabled={isSubmitting}>
                                    <option value="">{t('forms.select_institution', 'Selecione sua instituição')}</option>
                                    {INSTITUTION_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>

                            <ArrowButton type="submit" variant="pink" size="lg" className="cz-contact__submit" disabled={isSubmitting}>
                                {isSubmitting ? t('forms.sending', 'Enviando...') : t('forms.send_message', 'Enviar mensagem')}
                            </ArrowButton>

                            {(validationError || submitStatus === 'error') && (
                                <p className="cz-contact__error" role="alert">
                                    {validationError ?? t('forms.error', 'Erro ao enviar mensagem. Tente novamente.')}
                                </p>
                            )}
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
